import { SwapVert } from '@mui/icons-material'
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material'
import {
  ImproveSortColumnEnum,
  SortOrderEnum,
} from '@ulysses-inc/harami_api_client'
import React, { useState } from 'react'
import {
  DEFAULT_IMPROVES_SORT_ORDER,
  ImprovesSortOrder,
} from './improvesSortOrder'

interface OwnProps {
  getSortedImproves: (
    isClear: boolean,
    sortColumn: ImproveSortColumnEnum | undefined,
    sortOrder: SortOrderEnum,
  ) => void
  improvesSortOrder: ImprovesSortOrder
}

type SortDisplayValue =
  | { name: string; hasDirection: true; asc: string; desc: string }
  | { name: string; hasDirection: false }
type SortDisplayObject = Record<ImproveSortColumnEnum, SortDisplayValue>

const sortDisplay: SortDisplayObject = {
  [ImproveSortColumnEnum.ReportDate]: {
    name: '実施日',
    hasDirection: true,
    asc: '古い順',
    desc: '新しい順',
  },
  // ステータスは API 側で固定の進捗順。方向選択を提供しない。
  [ImproveSortColumnEnum.Status]: {
    name: 'ステータス',
    hasDirection: false,
  },
}

const isImproveSortColumn = (value: string): value is ImproveSortColumnEnum =>
  (Object.values(ImproveSortColumnEnum) as string[]).includes(value)

const ImprovesSortDropDown: React.FC<OwnProps> = (props: OwnProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = anchorEl !== null

  // localStorage 経由で想定外の値が混入し得るため、ランタイムでも検証する。
  // 不正値の場合はクリア相当として既定値 (実施日 DESC) にフォールバック。
  const rawSortColumn = props.improvesSortOrder.sortColumn
  const validSortColumn = isImproveSortColumn(rawSortColumn)
    ? rawSortColumn
    : DEFAULT_IMPROVES_SORT_ORDER.sortColumn
  const sortOrder = props.improvesSortOrder.sortOrder

  const changeSortColumn = (value: string) => {
    if (!isImproveSortColumn(value)) return
    // 実施日の既定は DESC (新しい順、既存挙動)。STATUS は API 側で固定なので方向は意味を持たないが、便宜上 DESC を渡す。
    props.getSortedImproves(false, value, SortOrderEnum.Desc)
  }

  const clearSort = () => {
    // 呼び出し側で既定値 (実施日 DESC) に戻す
    props.getSortedImproves(true, undefined, SortOrderEnum.Desc)
  }

  const changeSortOrder = (value: SortOrderEnum) => {
    props.getSortedImproves(false, validSortColumn, value)
  }

  const currentDisplay = sortDisplay[validSortColumn]
  const buttonLabel = currentDisplay.hasDirection
    ? `並び替え: ${currentDisplay.name}(${sortOrder === SortOrderEnum.Asc ? currentDisplay.asc : currentDisplay.desc})`
    : `並び替え: ${currentDisplay.name}順`

  const hoverArea = (
    <Stack sx={{ p: 2 }} spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={clearSort}>クリア</Button>
      </Box>
      <Select
        fullWidth
        size="small"
        value={validSortColumn}
        onChange={(e: SelectChangeEvent<string>) =>
          changeSortColumn(e.target.value)
        }
      >
        {(Object.keys(sortDisplay) as ImproveSortColumnEnum[]).map(key => (
          <MenuItem key={key} value={key}>
            {sortDisplay[key].name}
          </MenuItem>
        ))}
      </Select>
      {currentDisplay.hasDirection && (
        <RadioGroup
          value={sortOrder}
          onChange={e => changeSortOrder(e.target.value as SortOrderEnum)}
          sx={{ mt: 1.5 }}
        >
          <FormControlLabel
            value={SortOrderEnum.Desc}
            control={<Radio size="small" />}
            label={currentDisplay.desc}
          />
          <FormControlLabel
            value={SortOrderEnum.Asc}
            control={<Radio size="small" />}
            label={currentDisplay.asc}
          />
        </RadioGroup>
      )}
    </Stack>
  )

  return (
    <Box>
      <Button
        color="secondary"
        variant="outlined"
        startIcon={<SwapVert />}
        onClick={({ currentTarget }) => setAnchorEl(currentTarget)}
      >
        {buttonLabel}
      </Button>
      <Popover
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { width: '100%', maxWidth: 232 },
          },
        }}
      >
        {hoverArea}
      </Popover>
    </Box>
  )
}

export default ImprovesSortDropDown
